/**
 * 직원 상세 - 고용/계약 탭 (V55.2)
 *
 * Read-only: 모든 필드 수정 불가
 * Histories 기반으로 고용형태별 날짜 표시
 *
 * V55.2 변경사항:
 * - 현재 고용형태만 표시 → 모든 고용형태별 입사일 표시
 * - 예: 파견→정규 전환 시, "파견직 입사일", "정규직 입사일" 모두 표시
 */

import React from 'react';
import { FileText } from 'lucide-react';
import { Employee } from '../../../../shared/types/employee';
import { EMPLOYEE_STATUS_LABELS } from '../../../../shared/constants/employeeStatus';
import { useEmploymentHistories } from '../../../../shared/hooks/useEmploymentHistories';

interface EmploymentContractTabProps {
  employee: Employee;
}

export function EmploymentContractTab({ employee }: EmploymentContractTabProps) {
  const { histories, isLoading: historiesLoading } = useEmploymentHistories(employee.id);

  /**
   * V55.2: 모든 고용형태별 날짜 렌더링
   *
   * - 해당 직원의 모든 고용이력을 시간순(오래된 것→최신)으로 표시
   * - 각 고용형태별로 입사일(+만료일) 표시
   * - 예: 파견→정규 전환 시, "파견직 입사일", "파견직 만료일", "정규직 입사일" 모두 표시
   */
  const renderAllEmploymentDates = () => {
    if (historiesLoading) {
      return <div style={valueStyle}>로딩 중...</div>;
    }

    if (!histories || histories.length === 0) {
      return <div style={valueStyle}>(고용이력 없음)</div>;
    }

    // startDate 기준 정렬 (오름차순 - 오래된 것부터)
    const sortedHistories = [...histories].sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
      return dateA - dateB;
    });

    // 고용형태 코드 → 라벨 변환
    const getEmploymentTypeLabel = (code: string): string => {
      switch (code) {
        case 'DISPATCH': return '파견직';
        case 'INTERN': return '인턴';
        case 'CONTRACT': return '계약직';
        case 'REGULAR': return '정규직';
        default: return code || '기타';
      }
    };

    return sortedHistories.map((history, index) => {
      const { employmentTypeCode, startDate, endDate } = history;
      const label = getEmploymentTypeLabel(employmentTypeCode);

      // 정규직은 입사일만, 나머지는 입사일+만료일
      if (employmentTypeCode === 'REGULAR') {
        return (
          <InfoItem key={index} label={`${label} 입사일`} value={startDate || '(미등록)'} />
        );
      }

      return (
        <React.Fragment key={index}>
          <InfoItem label={`${label} 입사일`} value={startDate || '(미등록)'} />
          <InfoItem label={`${label} 만료일`} value={endDate || '(미등록)'} />
        </React.Fragment>
      );
    });
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <FileText size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={titleStyle}>고용/계약</h3>
        <span style={readOnlyBadgeStyle}>고용관리 메뉴에서 수정</span>
      </div>

      <div style={gridStyle}>
        <InfoItem label="고용형태" value={employee.currentEmploymentType || '-'} />
        <InfoItem label="재직상태" value={EMPLOYEE_STATUS_LABELS[employee.status || 'active']} />
        <InfoItem label="최초입사일" value={employee.firstHireDate || '-'} />
        {/* V62: 파견직은 엔글 최초입사일 미노출 */}
        {employee.currentEmploymentTypeCode !== 'DISPATCH' && (
          <InfoItem label="엔글 최초 입사일" value={employee.ngleFirstHireDate || '-'} />
        )}
        <InfoItem label="수습 만료일" value={employee.probationEndDate || '-'} />
      </div>

      <div style={{ marginTop: '20px' }}>
        <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>고용형태별 날짜</h4>
        <div style={gridStyle}>
          {renderAllEmploymentDates()}
        </div>
      </div>

      {employee.status === 'terminated' && employee.leaveDate && (
        <div style={{ marginTop: '20px', padding: '16px', background: '#fee', borderRadius: '8px' }}>
          <div style={labelStyle}>퇴사일</div>
          <div style={{ ...valueStyle, color: '#c00' }}>{employee.leaveDate}</div>
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value}</div>
    </div>
  );
}

const containerStyle: React.CSSProperties = { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid var(--color-gray-200)' };
const titleStyle: React.CSSProperties = { fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 };
const readOnlyBadgeStyle: React.CSSProperties = { marginLeft: 'auto', padding: '4px 12px', background: 'var(--color-gray-100)', color: 'var(--color-gray-600)', fontSize: '12px', fontWeight: '500', borderRadius: '12px' };
const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' };
const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: '500', color: 'var(--color-gray-500)', marginBottom: '4px' };
const valueStyle: React.CSSProperties = { fontSize: '14px', color: 'var(--color-gray-800)', fontWeight: '500' };
